import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Box, Button, FormHelperText, Link, Menu, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { cloneDeep, get, unset } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';

import { set } from 'lodash';
import { PostRecord } from '../../../lib/api/PostRecord';
import { APIS } from '../../../lib/constants/apis';
import { NACHRICHT_ORDNER, NOT_FOUND_ERROR_MESSAGE } from '../../../lib/constants/constant';
import { enumT, getFieldNamefromDataPath, isTrue } from '../../../utils/helper';
import CreateForm from '../form-component/create-form/CreateForm';
import LoadingBackdrop from '../loading/LoadingBackdrop';
import MuiSnackbar from '../mui-snackbar/MuiSnackbar';
import NachrichtFieldsValidator from './NachrichtFieldsValidator';

// import schemas
import { useAuthentication } from '../../../context/AuthContext';
import Schema from '../../../lib/paths/antrag_FULL_Paths.json';
import NachrichtSchema from '../../../lib/paths/nachricht_FULL_Paths.json';
let AP = Schema.attributes;
let NP = NachrichtSchema.attributes;

interface NachrichtFieldsProps {
	handleCloseDialog: () => void;
	applications: any[];
	setSendNachrichtSuccess: (value: boolean) => void;
	nachrichtId?: number;
	isReply?: boolean;
	originalTitle?: string;
	originalContent?: string;
}
const NachrichtFields: FC<NachrichtFieldsProps> = ({
	handleCloseDialog,
	applications,
	setSendNachrichtSuccess,
	nachrichtId,
	isReply,
	originalTitle,
	originalContent,
}) => {
	const { authenticatedData } = useAuthentication();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [selectedApplication, setSelectedApplication] = useState<any>({});
	const [formValues, setFormValues] = useState<Record<string, any>>({});
	const [formErros, setFormErros] = useState<Record<string, any>>({});
	const [wsErrorMsg, setWsErrorMsg] = useState<string>('');

	const applicationLists = useMemo(() => {
		if (applications.length > 0) return applications;
		else return [];
	}, [applications]);

	const handleInputChange = (path: string, value: any) => {
		setFormValues((prevValue) => ({
			...prevValue,
			[path]: value,
		}));
		let errorObject = cloneDeep(formErros);
		unset(errorObject, path);
		setFormErros(errorObject);
	};

	const validateFormValues = () => {
		let errorObj = {};
		let errs = NachrichtFieldsValidator.validate(formValues);
		for (let error of errs) {
			set(errorObj, error.path, get(error, 'message', ''));
		}
		setFormErros(errorObj);
		return Object.keys(errorObj).length === 0;
	};

	const handleSendNachricht = async () => {
		if (validateFormValues()) {
			const data = {
				data: {
					...formValues,
					[getFieldNamefromDataPath(NP.Allgemein_Ordner.path)]: NACHRICHT_ORDNER.Posteingang,
					[getFieldNamefromDataPath(NP.Antragsteller.path)]: get(authenticatedData, 'organisationId', -1),
					[getFieldNamefromDataPath(NP.Allgemein_Erstellt_Amum.path)]: new Date(),
					[getFieldNamefromDataPath(NP.Urspruengliche_Nachricht.path)]: isTrue(isReply) ? nachrichtId : null,
				},
			};
			if (isReply) set(data, 'data.UrspruenglicheNachricht', nachrichtId);
			setLoading(true);
			try {
				const res = await PostRecord(APIS['Portal-Nachricht'], data);
				let pushNotificationdata = { data: { entities: [{ api: 'nachrichts', id: get(res, 'data.id') }] } };
				await PostRecord(APIS.PushNotificaiton, pushNotificationdata);
				if (res) setSendNachrichtSuccess(true);
			} catch (err: any) {
				console.error('ERROR Saving Nachrichten ', err);
				setWsErrorMsg(err.message);
			} finally {
				handleCloseDialog();
				setLoading(false);
			}
		}
	};

	useEffect(() => {
		if (applicationLists.length) {
			if (applicationLists.length === 1) {
				setSelectedApplication(applicationLists[0]);
			}
			// Setting Initial Values
			setFormValues(() => ({
				Antrag: applicationLists.length === 1 ? get(applicationLists[0], 'id') : null,
				Allgemein_Betreff: isReply ? `Re: ${originalTitle}` : '',
				Allgemein_Inhalt:
					originalContent ?
						`${'\n\n-------------------------------------- Ursprügliche Nachricht --------------------------------------\n' + originalContent}`
					:	'',
			}));
		}
		setLoading(false);
	}, [applicationLists, isReply, originalContent, originalTitle]);

	if (loading) return <LoadingBackdrop />;
	return (
		<>
			<MuiSnackbar
				showSnackbar={Boolean(wsErrorMsg)}
				closeSnackbar={() => setWsErrorMsg('')}
				alertMessage={wsErrorMsg || NOT_FOUND_ERROR_MESSAGE}
				error={true}
			/>
			<Stack spacing={3}>
				<Box>
					<Link
						onClick={(e) => setAnchorEl(e.currentTarget)}
						sx={{
							cursor: 'pointer',
							display: 'inline-flex',
							alignContent: 'center',
							justifyContent: 'space-between',
							minWidth: '12.82rem',
							borderBottom: `${get(formErros, 'Antrag', false) ? '2px solid #d32f2f' : '2px solid'}`,
							color: `${get(formErros, 'Antrag', false) ? '2px solid #d32f2f' : null}`,
						}}
					>
						An: {'  '}
						{get(selectedApplication, AP.Zusammenfassung_Ueberblick_Titel.path) ?
							`${`[${enumT(get(selectedApplication, AP.Status_Status.path, 'ausstehend'))}] ${get(selectedApplication, AP.Zusammenfassung_Ueberblick_Titel.path)}`}`
						:	''}
						{anchorEl ?
							<KeyboardArrowUp />
						:	<KeyboardArrowDown />}
					</Link>
					{get(formErros, 'Antrag', false) && <FormHelperText error>{get(formErros, 'Antrag')}</FormHelperText>}

					<DropdownListApplication {...{ applicationLists, setSelectedApplication, handleInputChange, anchorEl, setAnchorEl }} />
				</Box>

				<CreateForm
					formStruc={[
						{
							fields: [
								{ path: 'Allgemein_Betreff' },
								{ type: 'text', path: 'Allgemein_Inhalt', placeholder: 'Schreiben Sie hier Ihre Nachricht' , maxCharLength: 2500},
							],
							userProps: { direction: 'column', columnToDivide: 1 },
						},
					]}
					formValues={formValues}
					updateFormValuesFunc={handleInputChange}
					formErrors={formErros}
					api={APIS['Portal-Nachricht']}
				/>
				<Stack direction={'row'} justifyContent={'space-between'}>
					<Button variant={'outlined'} onClick={handleCloseDialog}>
						{' '}
						Abbrechen
					</Button>
					<Button onClick={handleSendNachricht}>Abschicken</Button>
				</Stack>
			</Stack>
		</>
	);
};
export default NachrichtFields;

interface DropdownListApplicationProps {
	applicationLists: any[];
	setSelectedApplication: (app: Record<string, any>) => void;
	handleInputChange: (path: string, value: any) => void;
	anchorEl: HTMLElement | null;
	setAnchorEl: (anchorEl: HTMLElement | null) => void;
}
const DropdownListApplication: FC<DropdownListApplicationProps> = ({
	applicationLists,
	setSelectedApplication,
	handleInputChange,
	anchorEl,
	setAnchorEl,
}) => {
	const handleApplicationSelect = (app: Record<string, any>) => {
		setSelectedApplication(app);
		handleInputChange('Antrag', get(app, 'id'));
		setAnchorEl(null);
	};
	return (
		<>
			<Menu
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				MenuListProps={{
					'aria-labelledby': 'lock-button',
					role: 'listbox',
				}}
			>
				<TableContainer key={1}>
					<Table stickyHeader aria-label='sticky header table'>
						<TableHead>
							<TableRow>
								<TableCell>Antragstitel</TableCell>
								<TableCell>Status</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{applicationLists.map((ele, i) => (
								<TableRow key={i} hover onClick={() => handleApplicationSelect(ele)}>
									<TableCell>{enumT(get(ele, AP.Status_Status.path))}</TableCell>
									<TableCell>{enumT(get(ele, AP.Zusammenfassung_Ueberblick_Titel.path))}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Menu>
		</>
	);
};
